
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Action, State } from "vuex-class"

import Guid from "@/scripts/guid"

import { UserWithFields } from "@/types/user"
import { LabeledValue } from "@/types/labeledValue"
import { CaseDetails, Team, DataType, Field, CaseField, TemplateDetails } from "@/types"
import { CaseColumn, cachedUsersPlusDynamic } from "@/types/caseColumns"
import { Operator } from "@/types/insights"
import { dateRangeLabels, dateRangeRangeLabels } from "@/types/insights/dateRange"
import { DateClauseType } from "@/types/insights/dateClauseType"

import vSelect from "vue-select"

import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"

import DateTime from "@/components/Fields/DateTime.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import UserRole from "@/components/CaseDetails/Users/UserRole.vue"
import MapSearchBounds from "./MapSearchBounds.vue"
import DateTimeParameterValue from "./DateTimeParameterValue.vue"

import { getDefaultSearchClauseParameters, Clause, SearchClauseParameters, DayOfWeek } from "@/types/view"
import { DynamicValue } from "@/types/dynamic"
import { formatDateMixin, MONTH_NAMES } from "@/mixins/formatDateMixin"
import positionDropdown from "@/scripts/VSelectDropdownPosition.ts"
import { LocationLabels } from "@/types/location"
import SimpleUserSelect from "./SimpleUserSelect.vue"
import DataCache from "@/caching/caching"
import apiDefaultCache from "@/services/apiDefaultCache"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		datetime: DateTime,
		numeric: Numeric,
		"map-search": MapSearchBounds,
		"dataSourceSelect": DataSourceSelect,
		"date-time-param": DateTimeParameterValue,
		"user-role": UserRole,
		"simple-user-select": SimpleUserSelect
	}
})
export default class ParameterValue extends Vue {
	@Prop({ default: null })
	field!: CaseColumn;

	@Prop({ default: () => [] })
	allCaseFields!: CaseColumn[];

	@Prop({ default: null })
	value!: string | null;

	/** A list of currently selected field and values. Used to filter sub categories based on category selected. */
	@Prop({ type: Object, default: () => ({ }) })
	selectedFields!: Record<string, LabeledValue>;

	@Prop({ default: false })
	isNewClause!: boolean;

	@Prop({ default: () => Guid.newGuid() })
	clauseIndex!: number | string;

	@Prop({ default: null })
	operator!: Operator | null;

	@Prop({ default: true })
	dynamicValue!: boolean;

	@Prop({ default: true })
	usersAllowDynamic!: boolean;

	@Prop({ default: false })
	userRolesAllowTeams!: boolean;

	@Prop({ default: () => null })
	searchClauseParameters!: SearchClauseParameters;

	@Prop({ default: false })
	showCollapsed!: boolean

	@Prop({ default: false })
	setFieldValue!: boolean

	@Prop({ default: false })
	allowMultiple!: boolean

	@Prop({ default: true })
	allowDynamic!: boolean

	@Prop({ default: false })
	liveFiltering!: boolean

	@Prop({ type: String, default: null })
	templateId!: string | null

	@Prop({ type: Boolean })
	utcDates!: boolean

	@Prop({ type: Boolean, default: false })
	isFiltering!: boolean

	@Prop({ type: Boolean, default: false })
	excludeInactiveUsers!: boolean

	@Prop({ type: Boolean, default: false })
	hideTimeSpentForUserRole!: boolean

	@Prop({ default: false })
	disabled!: boolean

	@setup.State
	fields!: Field[];
	@setup.State teams!: Team[]

	@setup.Action loadFieldsIfUnloaded!: () => Promise<any[]>;
	@setup.Action loadTeamsIfUnloaded!: () => Promise<void>;

	@State private readonly userCache!: DataCache<UserWithFields>
	@State private readonly templateCache!: DataCache<TemplateDetails>

	@Action private loadTenantSettingsIfUnloaded!: () => Promise<void>

	private selectedSearchClauseParameters: SearchClauseParameters = this.searchClauseParameters;

	private dateClauseType: DateClauseType | null = null;

	public isDynamicValue : boolean = true

	private timeSpentDataSourceId: string = "";
	private isMounted: boolean = false;

	userOptions: (UserWithFields | any)[] | undefined

	async mounted () {
		this.isDynamicValue = this.dynamicValue
		const loadUsersPromise = this.userCache.fetchIfNeeded()
		const loadTenantSettingsPromise = this.loadTenantSettingsIfUnloaded()
		this.selectedSearchClauseParameters = this.searchClauseParameters ? this.searchClauseParameters : getDefaultSearchClauseParameters()

		const loadFieldsPromise = this.loadFieldsIfUnloaded()
		const loadTemplatesPromise = this.templateCache.fetchIfNeeded()
		const loadTeamsPromise = this.loadTeamsIfUnloaded()

		if (this.fieldDataType === DataType.timeSpentAsValue) {
			const trackingFieldId = this.field.fieldExtensions["trackingFieldId"]
			if (this.isNewClause) {
				this.selectedSearchClauseParameters.timeSpentClause.trackingFieldId = trackingFieldId
			}

			this.timeSpentDataSourceId = this.getTrackedFieldDataSourceId(trackingFieldId)
		}

		if (this.isNewClause) {
			this.dynamicValueChanged(true)
		}

		if (this.fieldDataType === DataType.dateTime) {
			this.dateClauseType = DateClauseType.RelativeCurrentDate
		}

		await Promise.allSettled([loadUsersPromise, loadTenantSettingsPromise,
			loadFieldsPromise, loadTeamsPromise, loadTemplatesPromise])

		if (this.fieldDataType === DataType.auditUser) {
			await this.loadUserOptions()
			this.selectedItem = this.userOptions?.find((i: any) => i.id === this.value) ?? null
		}

		// for some child components, the relevant props are only set in ParameterValue.vue when it has finished mounting (code directly above), so need to wait until it has fully mounted before rendering children
		this.isMounted = true
	}

	private Operator = Operator;
	private DataType = DataType;
	private DateClauseType = DateClauseType;

	private calcDropdownPosition = positionDropdown;

	private fieldValueKey: string | null = null;
	private returnedFieldOptions: any[] = [];

	ranges: any[] = [];
	private rangeFilter: string | null = null;

	private selectedItem = null;

	private selectedDayOfWeek: string | null = null;

	/** Builds a CaseDetails record from this.selectedFields to be passed to DataSourceSelect */
	private get currentCase () {
		var allFields = this.fields

		var caseFields: {[key: string]: CaseField} = {}

		// Build up an object containing all 'selectedFields' as an object with each member being of the type [key: string]: CaseField.
		if (this.selectedFields) {
			var caseFieldsKeys = Object.keys(this.selectedFields)
			caseFieldsKeys.forEach(key => {
				var field = allFields.find(field => field.id === key)

				if (!field) { return }

				// CaseField
				caseFields[key] = {
					oldValue: null,
					value: this.selectedFields[key].value,
					label: this.selectedFields[key].label,
					valueObject: null,
					section: "",
					order: 0,
					field: field,
					requiredCase: false,
					requiredRecord: false
				}
			})
		}

		// Build up a blank CaseDetails with just the fields member.
		var caseDetails: CaseDetails = {
			id: "",
			caseNumber: "",
			title: "",
			statusId: "",
			severityId: "",
			templateId: null,
			templateTitle: null,
			assigneeId: null,
			openedByUserId: null,
			isCase: false,
			isClosed: false,
			isSensitive: false,
			lastUpdatedAt: null,
			categoryTitle: null,
			statusTitle: "",
			severityTitle: "",
			severityLevel: 0,
			openedByUser: null,
			areas: [],
			summary: "",
			fields: caseFields,
			sections: [],
			sectionDetails: undefined,
			links: [],
			actions: [],
			permissions: null,
			tags: null,
			users: [],
			cadenceDetails: null,
			scheduling: undefined,
			builtInFieldLocks: null,
			pendingTeams: [],
			viewingUsers: [],
			cadenceEnabled: false,
			hiddenFromInsights: false
		}

		return caseDetails
	}

	private get fieldAsFieldType () {
		var field = this.fields.find(f => f.id === this.field.key)

		if (field) { return field } else { return undefined }
	}

	public get fieldDataType (): DataType | null {
		if (!this.field) return null

		return this.field.comparisonType !== undefined
			? this.field.comparisonType
			: this.field.fieldDataType
				? this.field.fieldDataType
				: null
	}

	public dateClauseTypeChanged (newDateClauseType: DateClauseType): void {
		this.dateClauseType = newDateClauseType
		this.$emit("dateClauseTypeChanged", this.dateClauseType)
	}

	public get selectedDateClauseType (): DateClauseType | null {
		return this.dateClauseType
	}

	private get daysOfWeekNames (): string[] {
		return Object.values(DayOfWeek).filter(value => typeof value === "string") as string[]
	}

	private get dynamicInputValue (): DynamicValue | null {
		return this.value
			? {
				value: this.value,
				dynamic: this.isDynamicValue
			}
			: null
	}

	private get dateTimeText (): string {
		if (!this.field || this.value == null) return "Select value"

		if (this.isDynamicValue) {
			let dynamicInt = null

			switch (this.dateClauseType) {
				case DateClauseType.ExactDate:
					const date = this.value ? new Date(this.value) : null
					if (!date) {
						return "Invalid date"
					}
					const dateString = (date.getUTCMonth() + 1) + "/" + date.getUTCDate() + "/" + date.getUTCFullYear()
					return dateString
				case DateClauseType.MonthRange:
					let currentStartDate = this.selectedSearchClauseParameters && this.selectedSearchClauseParameters.startMonthRange
					let currentEndDate = this.selectedSearchClauseParameters && this.selectedSearchClauseParameters.endMonthRange
					if (!currentStartDate || !currentEndDate) {
						return "Pick value"
					}
					let startLabel = MONTH_NAMES[currentStartDate.getMonth()] + " " + currentStartDate.getFullYear().toString()
					let endLabel = MONTH_NAMES[currentEndDate.getMonth()] + " " + currentEndDate.getFullYear().toString()
					if (startLabel !== endLabel) {
						return startLabel + " - " + endLabel + " inclusive"
					} else {
						return "In " + startLabel
					}
				default:
					dynamicInt = parseInt(this.value)
					if (isNaN(dynamicInt)) {
						if (this.operator === Operator.DateRange) {
							if (this.value === "WeekNumber") {
								if (!this.selectedSearchClauseParameters.weekNumber) {
									return "Pick value"
								}
								return `Week #${(this.selectedSearchClauseParameters && this.selectedSearchClauseParameters.weekNumber
									? this.selectedSearchClauseParameters.weekNumber
									: "(Unselected)")} of the year`
							} else if (this.value === "DateRange" && this.selectedSearchClauseParameters) {
								if (!this.selectedSearchClauseParameters.startDate || !this.selectedSearchClauseParameters.endDate) {
									return "Pick value"
								}
								return (this.selectedSearchClauseParameters.startDate
									? formatDateMixin.methods.displayUtcDateOnlyFromISOString(this.selectedSearchClauseParameters.startDate!.toISOString())
									: "(Unselected)") +
									" - " + (this.selectedSearchClauseParameters.endDate
									? formatDateMixin.methods.displayUtcDateOnlyFromISOString(this.selectedSearchClauseParameters.endDate!.toISOString())
									: "(Unselected)")
							}
							return dateRangeRangeLabels.findAndGet(d => d.id === this.value, d => d.title, "")
						} else {
							return dateRangeLabels.findAndGet(d => d.id === this.value, d => `Start of ${d.title}`, "")
						}
					} else if (dynamicInt === 0) {
						return "Today"
					} else if (dynamicInt > 0) {
						return dynamicInt + " days from now"
					} else {
						return Math.abs(dynamicInt) + " days ago"
					}
			}
		}

		return typeof this.value === "string"
			? formatDateMixin.methods.displayDateOnlyFromISOString(this.value)
			: formatDateMixin.methods.displayDateOnlyFromJsDate(this.value)
	}

	public get fieldOptions (): any[] {
		if (!this.isMounted) return []

		if (this.field && (this.field.dataType === "date" || this.field.dataType === "date-time" ||
			this.field.fieldDataType === DataType.dateTime)) {
			this.returnedFieldOptions = dateRangeLabels
		}

		if (this.field && this.field.columnValues && this.fieldValueKey !== this.field.key) {
			this.fieldValueKey = this.field.key
			this.returnedFieldOptions = []

			// load cached data where possible to avoid duplicate loading of data
			if (this.field.key === "templateId") {
				this.returnedFieldOptions = this.templateCache.items!
			} else {
				apiDefaultCache.columnValuesForField(this.field.key, this.field.columnValues)
					.then(options => this.returnedFieldOptions = options as unknown[])
			}
		}

		if (this.field && (this.field.dataType === "user-role" || this.field.dataType === "subrole")) {
			if (this.operator === Operator.InTeam) {
				this.fieldValueKey = null
				if (this.teams.length === 0) {
					this.returnedFieldOptions = []
					this.loadTeamsIfUnloaded()
				}

				this.returnedFieldOptions = this.teams.map(t => {
					return {
						id: t.id,
						name: t.title
					}
				})
			} else if (this.operator === Operator.UserTimeLocation) {
				this.returnedFieldOptions = [
					{
						id: "null",
						name: "Not Set"
					},
					...LocationLabels.map(l => {
						return {
							id: l.key.toString(),
							name: l.label
						}
					})
				]
			} else if (this.fieldValueKey !== this.field.key) {
				this.fieldValueKey = this.field.key
				this.returnedFieldOptions = []

				if (this.usersAllowDynamic) {
					cachedUsersPlusDynamic(this.userCache.items, this.userCache.fetchIfNeeded)
						.then(options => this.returnedFieldOptions = options)
				} else {
					this.returnedFieldOptions = this.userCache.items
				}

				const allUsers = this.userCache.items;

				(this.usersAllowDynamic ? cachedUsersPlusDynamic(allUsers, this.userCache.fetchIfNeeded) : new Promise((resolve, reject) => resolve(allUsers))).then((options: any) => {
					if (this.userRolesAllowTeams) {
						this.returnedFieldOptions = [
							...options,
							...this.teams.map(t => {
								return {
									id: t.id,
									name: t.title
								}
							})
						].sortBy("title")
					} else {
						this.returnedFieldOptions = options
					}
				})
			}
		} else if (this.field && (this.field.dataType === "auditUser")) {
			if (this.operator === Operator.Equals) {
				this.fieldValueKey = this.field.key
				this.returnedFieldOptions = []

				if (this.usersAllowDynamic) {
					cachedUsersPlusDynamic(this.userCache.items, this.userCache.fetchIfNeeded)
						.then(options => this.returnedFieldOptions = options)
				} else {
					this.returnedFieldOptions = this.userCache.items
				}
			}
		}

		if (this.value && !this.selectedItem) {
			this.selectedItem = this.returnedFieldOptions.find(i => i.id === this.value)
		}

		return this.returnedFieldOptions
	}

	getTrackedFieldDataSourceId (trackedFieldId: string) : string {
		if (!this.allCaseFields) {
			Error("Missing dependent case fields")
		}

		const trackedField = this.allCaseFields.find(c => c.fieldId === trackedFieldId)
		if (!trackedField || !trackedField.dataSourceId) {
			throw Error("Missing time tracked field Data Source ID")
		}

		return trackedField!.dataSourceId!
	}

	searchClauseParametersChanged (searchClauseParameters: SearchClauseParameters) {
		this.selectedSearchClauseParameters = searchClauseParameters
		this.$emit("searchClauseParametersChanged", searchClauseParameters)
	}

	timeSpentListValueChanged (listValue: string) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.searchValue = listValue
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("input", listValue)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	timeSpentListLabelChanged (listLabel: string) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.searchLabel = listLabel
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("inputLabel", listLabel)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	timeSpentHoursChanged (newHoursValue: number) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.hoursSpent = newHoursValue
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	@Watch("value")
	ValuePropChanged (newValue: string | null) {
		if (this.selectedItem && !newValue) {
			this.selectedItem = null
		} else {
			if (this.fieldDataType === DataType.auditUser && !!this.userOptions) {
				this.selectedItem = this.userOptions.find((i: any) => i.id === this.value)
			} else {
				this.selectedItem = this.returnedFieldOptions.find(i => i.id === this.value)
			}
			if (this.selectedItem === undefined) {
				this.selectedItem = null
			}
		}
	}

	@Watch("dynamicValue")
	DynamicValueChanged (newDynamicValue: boolean) {
		this.isDynamicValue = newDynamicValue ?? true
	}

	valueChanged (newValue: string | null) {
		this.$emit("input", newValue)
		this.$emit("dynamicInput", {
			value: newValue,
			dynamicValue: false
		})
	}

	valueItemChanged (newValue: any) {
		const dynamicValue = newValue && newValue.dynamicValue ? newValue.dynamicValue : false
		if (newValue && dynamicValue !== this.isDynamicValue) {
			this.$emit("dynamicValueChanged", dynamicValue)
		}

		this.$emit("dynamicInput", {
			value: newValue ? newValue.id : null,
			dynamic: dynamicValue
		})

		this.$emit("input", newValue ? newValue.id : null)
		this.$emit("inputValue", newValue)

		const labelProperty = this.field.listLabel
			? this.field.listLabel
			: this.field.dataType === "user-role"
				? "name"
				: "label"
		if (newValue && newValue[labelProperty]) {
			this.$emit("inputLabel", newValue[labelProperty])
		} else {
			this.$emit("inputLabel", null)
		}
		if (this.selectedItem && !newValue) {
			this.selectedItem = null
		}
	}

	labelChanged (newLabel: string | null) {
		this.$emit("inputLabel", newLabel)
	}

	numberValueChanged (newValue: number) {
		const value = newValue === null || newValue === undefined ? null : newValue.toString()
		this.$emit("input", value)
		this.$emit("dynamicInput", {
			value: value,
			dynamic: false
		})
	}

	booleanValueChanged (newValue: boolean) {
		this.$emit("input", newValue)
	}

	dynamicValueChanged (newValue: boolean) {
		this.$emit("dynamicValueChanged", newValue === null || newValue === undefined ? false : newValue)
	}

	mapBoundsChanged (newValue: {lat: number, lng: number}[]) {
		this.$emit("input", JSON.stringify(newValue))
		this.$emit("inputLabel", "Bounds")
	}

	dynamicInputValueChanged (newValue: DynamicValue | null) {
		this.isDynamicValue = newValue ? newValue?.dynamic : true
		this.$emit("dynamicInput", newValue)
		this.$emit("dynamicValueChanged", newValue !== null && newValue.dynamic ? newValue.dynamic : false)

		this.$emit("input", newValue !== null && newValue.value !== null
			? newValue!.value instanceof Date ? newValue!.value.toISOString() : newValue!.value.toString()
			: null)
	}

	private get getDataCyLabel (): string {
		if (!this.field.label || this.field.label.length === 0 || this.field.label === " ") {
			return "parameter-value-" + this.field.key
		}
		// replace all instances of whitespace or '/' characters with '-'
		return "parameter-value-" + this.field.label.replace(/[' ']|[/]/g, "-").toLowerCase()
	}

	get placeholderText (): string {
		return this.isFiltering ? "Filter" : ""
	}

	async loadUserOptions () {
		const options = await (this.usersAllowDynamic
			? cachedUsersPlusDynamic(this.userCache.items, this.userCache.fetchIfNeeded)
			: this.userCache.items)

		this.userOptions = options as (UserWithFields | any)[]
	}

	showDataSourceSelect (field : CaseColumn) {
		return field.dataSourceId && this.fieldDataType !== DataType.timeSpentAsValue && !(this.fieldDataType === DataType.arrayData && this.operator === Operator.Contains)
	}
}
